/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Snackbar,
    Alert,
    Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../../Components/Sidebar/Sidebar';
import Navbar from '../../Components/Navbar/Navbar';
import { tableAPI } from '../../services/apiService';
import './Tables.css';

const convertPersianToEnglishDigits = (str) => {
    if (str === null || str === undefined) return '';
    return String(str).replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d));
};

const convertEnglishToPersianDigits = (str) => {
    if (str === null || str === undefined) return '';
    return String(str).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]);
};
function Tables() {
    const { t, i18n } = useTranslation();
    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [editingTable, setEditingTable] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const [searchTerm, setSearchTerm] = useState('');

    const navigate = useNavigate();
    const location = useLocation();
    useEffect(() => {
        if (location.state?.successMessage) {
            setSnackbar({
                open: true,
                message: location.state.successMessage,
                severity: 'success',
            });
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    useEffect(() => {
        const fetchTables = async () => {
            if (!localStorage.getItem('token') || !localStorage.getItem('userGroup')) {
                setError(t('auth_or_group_missing'));
                setLoading(false);
                return;
            }

            try {
                const result = await tableAPI.getAll({ fallback: [] });
                setTables(result || []);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchTables();
    }, [t]);

    const confirmDelete = (id) => {
        setSelectedId(id);
        setOpenDialog(true);
    };

    const handleCloseDelete = () => {
        setOpenDialog(false);
        setSelectedId(null);
    };

    const handleConfirmDelete = async () => {
        if (!selectedId) return;

        try {
            await tableAPI.remove(selectedId);
            setTables((prev) => prev.filter((tbl) => tbl.id !== selectedId));

            setSnackbar({
                open: true,
                message: t('delete_table_success'),
                severity: 'success',
            });
        } catch (err) {
            setSnackbar({
                open: true,
                message: t('delete_table_error'),
                severity: 'error',
            });
        } finally {
            handleCloseDelete();
        }
    };

    const handleEdit = (tbl) => {
        setEditingTable({ ...tbl });
        setOpenEditDialog(true);
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditingTable((prev) => ({
            ...prev,
            [name]: name === 'seats_number' ? value.replace(/[^\d۰-۹]/g, '') : value,
        }));
    };

    const handleSwitchChange = (e) => {
        const { checked } = e.target;
        setEditingTable((prev) => ({
            ...prev,
            free: checked,
        }));
    };

    const handleSaveEdit = async () => {
        if (!editingTable) return;

        try {
            const { id } = editingTable;
            const body = {
                table_id: editingTable.table_id,
                seats_number: Number(convertPersianToEnglishDigits(editingTable.seats_number)),
                free: editingTable.free,
            };

            const updatedTable = await tableAPI.update(id, body);

            setTables((prev) =>
                prev.map((type) => (type.id === id ? { ...type, ...updatedTable } : type))
            );

            setSnackbar({
                open: true,
                message: t('edit_table_success'),
                severity: 'success',
            });

            setOpenEditDialog(false);
            setEditingTable(null);
        } catch (err) {
            setSnackbar({
                open: true,
                message: t('edit_table_error'),
                severity: 'error',
            });
        }
    };

    const handleCloseSnackbar = (_, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    return (
        <div className="tables_page">
            <Sidebar />
            <div className="tables_main">
                <Navbar />
                <div className="tables_content">
                    <div className="tables_header">
                        <Button
                            variant="contained"
                            className="add_btn"
                            startIcon={<AddIcon />}
                            onClick={() => navigate('/tables/addnew')}
                        >
                            {t('add_table')}
                        </Button>
                        <div className="search_bar">
                            <input
                                type="text"
                                placeholder={t('search')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="user_search_input"
                            />
                        </div>
                    </div>

                    {loading && <p>{t('loading')}</p>}
                    {error && (
                        <p>
                            {t('error')}: {error}
                        </p>
                    )}

                    {!loading && !error && (
                        <TableContainer component={Paper} className="tables_table">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('table_number')}</TableCell>
                                        <TableCell>{t('seats_number')}</TableCell>
                                        <TableCell>{t('status')}</TableCell>
                                        <TableCell>{t('qr_code')}</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tables
                                        .filter((tbl) =>
                                            tbl.table_id
                                                .toLowerCase()
                                                .includes(searchTerm.toLowerCase())
                                        )
                                        .map((tbl) => (
                                            <TableRow key={tbl.id}>
                                                <TableCell data-label={t('table_number')}>
                                                    {tbl.table_id}
                                                </TableCell>

                                                <TableCell data-label={t('seats_number')}>
                                                    {i18n.language === 'fa'
                                                        ? convertEnglishToPersianDigits(
                                                              tbl.seats_number
                                                          )
                                                        : convertPersianToEnglishDigits(
                                                              tbl.seats_number
                                                          )}
                                                </TableCell>

                                                <TableCell data-label={t('status')}>
                                                    {tbl.free ? t('table_free') : t('table_busy')}
                                                </TableCell>

                                                <TableCell data-label={t('qr_code')}>
                                                    {tbl.qr_code ? (
                                                        <a
                                                            href={tbl.qr_code}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {t('view_qr')}
                                                        </a>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </TableCell>

                                                <TableCell
                                                    data-label={t('actions')}
                                                    className="table_actions"
                                                >
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        onClick={() => confirmDelete(tbl.id)}
                                                    >
                                                        {t('delete')}
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        color="success"
                                                        onClick={() => handleEdit(tbl)}
                                                    >
                                                        {t('edit')}
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    <Dialog open={openDialog} onClose={handleCloseDelete}>
                        <DialogTitle>{t('delete_confirm_title')}</DialogTitle>
                        <DialogContent>
                            <DialogContentText> {t('delete_table_confirm')} </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDelete} color="primary">
                                {t('no')}
                            </Button>
                            <Button onClick={handleConfirmDelete} color="error" autoFocus>
                                {t('yes_delete')}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog
                        open={openEditDialog}
                        onClose={() => setOpenEditDialog(false)}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle>{t('edit_table')}</DialogTitle>
                        <DialogContent>
                            {editingTable && (
                                <>
                                    <p>{t('table_number')}</p>
                                    <TextField
                                        margin="dense"
                                        placeholder={t('table_number')}
                                        name="table_id"
                                        value={editingTable.table_id || ''}
                                        onChange={handleEditChange}
                                        fullWidth
                                        variant="outlined"
                                    />
                                    <p>{t('seats_number')}</p>
                                    <TextField
                                        margin="dense"
                                        placeholder={t('enter_seats_number')}
                                        name="seats_number"
                                        type="text"
                                        value={editingTable.seats_number || ''}
                                        onChange={handleEditChange}
                                        fullWidth
                                        variant="outlined"
                                    />
                                    <div
                                        style={{
                                            marginTop: 16,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                        }}
                                    >
                                        <span>{t('status')}:</span>
                                        <Switch
                                            checked={Boolean(editingTable.free)}
                                            onChange={handleSwitchChange}
                                            color="primary"
                                        />
                                        <span>
                                            {editingTable.free ? t('table_free') : t('table_busy')}
                                        </span>
                                    </div>
                                </>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setOpenEditDialog(false)}>{t('cancel')}</Button>
                            <Button onClick={handleSaveEdit} color="success">
                                {t('save_changes')}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={4000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </div>
            </div>
        </div>
    );
}

export default Tables;
